/**
 * Setting a password from a one-time link.
 *
 * Public and unauthenticated by necessity — whoever lands here cannot sign in,
 * which is the entire point. The link is the credential.
 *
 * The token is read from the query string and never displayed. It is already in
 * the address bar, but echoing it into the page puts it in screenshots and
 * screen shares too, and this one is worth exactly one account.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';

/** Matches the server. Stated up front rather than after a rejected attempt. */
const MIN_PASSWORD_LENGTH = 10;

function describeWeakness(password: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `At least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  // Mirrors the server's rule: letters alone or digits alone are refused.
  if (/^\d+$/.test(password) || /^[A-Za-z]+$/.test(password)) {
    return 'Mix letters with numbers or symbols.';
  }
  if (new TextEncoder().encode(password).length > 72) {
    return 'Too long — bcrypt stops at 72 bytes.';
  }
  return null;
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);

  const weakness = password ? describeWeakness(password) : null;
  const mismatched = confirm.length > 0 && confirm !== password;
  const ready = password.length > 0 && !weakness && !mismatched && confirm === password;

  const submit = useMutation({
    mutationFn: async () =>
      (await client.post('/api/v1/auth/reset-password', {
        token,
        newPassword: password,
      })).data,
    onSuccess: () => {
      setDone(true);
      // Nothing is signed in yet — the reset deliberately ends every session,
      // including any this browser had.
      setTimeout(() => navigate('/login'), 2500);
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not set that password.')),
  });

  if (!token) {
    return (
      <Shell>
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600" />
          <div>
            <h1 className="text-sm font-semibold text-gray-900">
              This Link Is Incomplete
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Open the full link you were sent, or ask for a new one.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
          <div>
            <h1 className="text-sm font-semibold text-gray-900">Password Set</h1>
            <p className="mt-1 text-sm text-gray-600">
              Taking you to the sign-in page.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-sm font-semibold text-gray-900">Choose a Password</h1>
      <p className="mt-1 text-sm text-gray-600">
        This link works once and expires within the hour. Setting a password
        here signs out every device already using this account.
      </p>

      <form
        className="mt-4 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) submit.mutate();
        }}
      >
        <label className="block text-xs text-gray-600">
          New Password
          <input
            className="input mt-1"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            required
          />
        </label>
        {weakness && <p className="text-xs text-amber-700">{weakness}</p>}

        <label className="block text-xs text-gray-600">
          Confirm
          <input
            className="input mt-1"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </label>
        {mismatched && <p className="text-xs text-red-700">These do not match.</p>}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={!ready || submit.isPending}
        >
          {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Set password
        </button>
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
        {children}
      </div>
    </div>
  );
}
